import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DollarSign,
  CreditCard,
  Search,
  Plus,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  Users,
  FileText,
  X,
  Save,
  ChevronLeft,
  ChevronRight,
  Printer,
} from "lucide-react";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Pagination from "../../components/common/Pagination";
import ReceiptModal from "../../components/receipts/ReceiptModal";
import PromptPaymentModal from "../../components/payments/PromptPaymentModal";
import { getStudents, getStudentPaymentHistory } from "../../services/students";
import { StudentWithUserInfo } from "../../types/student";
import {
  generateReceiptNumber,
  generatePaymentReference,
} from "../../utils/receiptUtils";
import { getAcademicTerms } from "../../services/academicTerms";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface StudentFeeInfo {
  student: StudentWithUserInfo;
  totalDue: number;
  totalPaid: number;
  balance: number;
  status: "paid" | "partial" | "unpaid" | "overdue";
  feeStatus: string;
  scholarshipAmount: number;
}

const SecretaryFees: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "unpaid" | "partial" | "paid" | "overdue"
  >("all");
  const [selectedTermId, setSelectedTermId] = useState<string>("all");
  const [viewType, setViewType] = useState<"total" | "monthly">("total");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [promptPaymentModalOpen, setPromptPaymentModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] =
    useState<StudentWithUserInfo | null>(null);
  const [selectedStudentForPromptPayment, setSelectedStudentForPromptPayment] =
    useState<StudentWithUserInfo | null>(null);
  const [selectedStudentForDetails, setSelectedStudentForDetails] =
    useState<StudentWithUserInfo | null>(null);
  const [lastPaymentData, setLastPaymentData] = useState<{
    student: StudentWithUserInfo;
    payment: {
      amount: number;
      paymentMethod: string;
      referenceNumber?: string;
      paymentDate: string;
      notes?: string;
    };
    receiptNumber: string;
  } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    "cash" | "bank_transfer" | "cheque" | "mpesa" | "card" | "online"
  >("cash");
  const [paymentReference, setPaymentReference] = useState("");

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<"excel" | "pdf">("excel");
  const [exportScope, setExportScope] = useState<"filtered" | "all">(
    "filtered"
  );
  const [isExporting, setIsExporting] = useState(false);

  const queryClient = useQueryClient();

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchTerm, filterStatus, selectedTermId]);

  // Fetch academic terms for filtering
  const { data: academicTermsData } = useQuery({
    queryKey: ["academicTerms"],
    queryFn: getAcademicTerms,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch branch student fee summaries with monthly/total filtering
  const { data: feeSummariesData, isLoading: feeSummariesLoading } = useQuery({
    queryKey: [
      "branchStudentFeeSummaries",
      {
        view: viewType,
        month: selectedMonth,
        year: selectedYear,
        ...(viewType === "monthly" && {
          page: currentPage,
          limit: itemsPerPage,
        }),
      },
    ],
    queryFn: async () => {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const params = new URLSearchParams({
        view: viewType,
        ...(viewType === "monthly" && {
          month: selectedMonth.toString(),
          year: selectedYear.toString(),
          page: currentPage.toString(),
          limit: itemsPerPage.toString(),
        }),
      });

      const response = await fetch(
        `${API_URL}/api/fees/branch/student-summaries?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch fee summaries");
      }

      return response.json();
    },
    staleTime: 1 * 60 * 1000,
  });

  // Fetch students with their fee information (for pagination and search)
  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: [
      "students",
      { page: currentPage, limit: itemsPerPage, search: activeSearchTerm },
    ],
    queryFn: () =>
      getStudents({
        page: currentPage,
        limit: itemsPerPage,
        search: activeSearchTerm,
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch payment history for selected student
  const { data: paymentHistoryData, isLoading: paymentHistoryLoading } =
    useQuery({
      queryKey: ["studentPaymentHistory", selectedStudentForDetails?._id],
      queryFn: () => getStudentPaymentHistory(selectedStudentForDetails!._id),
      enabled: !!selectedStudentForDetails,
      staleTime: 1 * 60 * 1000,
    });

  // Record manual payment mutation - uses new payment reconciliation system
  const recordPaymentMutation = useMutation({
    mutationFn: async (data: {
      studentId: string;
      amount: number;
      paymentMethod: string;
      reference: string;
      notes?: string;
    }) => {
      // Make API call to new reconciliation endpoint
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${API_URL}/api/fees/payments/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          studentId: data.studentId,
          amount: data.amount,
          paymentMethod: data.paymentMethod,
          referenceNumber: data.reference,
          paymentDate: new Date().toISOString(),
          notes: data.notes || `Manual payment recorded by secretary`,
        }),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use the status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (response, variables) => {
      // Display reconciliation results
      const { data } = response;
      const {
        invoicesPaid,
        invoicesUpdated,
        creditAmount,
        appliedToInvoices,
        totalAmount,
      } = data;

      let successMessage = `Payment of KES ${totalAmount.toLocaleString()} recorded successfully.`;
      if (invoicesPaid > 0) {
        successMessage += ` Paid ${invoicesPaid} invoice(s) in full.`;
      }
      if (invoicesUpdated > invoicesPaid) {
        successMessage += ` Updated ${
          invoicesUpdated - invoicesPaid
        } partial payment(s).`;
      }
      if (creditAmount > 0) {
        successMessage += ` Credit balance: KES ${creditAmount.toLocaleString()}.`;
        toast.success(successMessage, { duration: 6000 });
      } else {
        toast.success(successMessage, { duration: 4000 });
      }

      // Prepare payment data for receipt
      const receiptNumber = data.receiptNumber;
      const paymentData = {
        student: selectedStudent!,
        payment: {
          amount: totalAmount,
          paymentMethod: variables.paymentMethod,
          referenceNumber: receiptNumber,
          paymentDate: new Date().toISOString(),
          notes: variables.notes,
        },
        receiptNumber,
      };

      setLastPaymentData(paymentData);
      setPaymentModalOpen(false);
      setSelectedStudent(null);
      setPaymentAmount("");
      setPaymentReference("");

      // Show receipt modal
      setReceiptModalOpen(true);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["students"] });
    },
    onError: (error: any) => {
      console.error("Payment error:", error);
      toast.error(error.message || "Failed to record payment");
    },
  });

  // Process students with their fee information from the filtered summaries
  const studentFeeInfo = useMemo(() => {
    if (!studentsData?.data) {
      return [];
    }

    // For monthly view, use feeSummariesData if available; for total view, use student.fees from aggregation
    if (viewType === "monthly" && feeSummariesData?.data) {
      // Monthly view - use month-specific data from feeSummariesData
      // Backend returns: { student: {...}, expected, paid, balance, feesCount }
      return feeSummariesData.data
        .map((summary: any) => {
          // summary.student already contains all the student info from backend
          const student = summary.student;

          if (!student) return null;

          const totalDue = summary.expected || 0;
          const totalPaid = summary.paid || 0;
          const balance = summary.balance || 0;

          let status: "paid" | "partial" | "unpaid" | "overdue" = "unpaid";
          if (balance === 0 && totalDue > 0) {
            status = "paid";
          } else if (totalPaid > 0 && balance > 0) {
            status = "partial";
          } else if (balance < 0) {
            status = "overdue";
          }

          return {
            student,
            totalDue,
            totalPaid,
            balance,
            status,
            feeStatus: summary.feeStatus || status,
            scholarshipAmount: 0,
          };
        })
        .filter((info: any) => {
          if (!info) return false;

          // Filter by search term
          const matchesSearch =
            !activeSearchTerm ||
            info.student.userInfo?.firstName
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase()) ||
            info.student.userInfo?.lastName
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase()) ||
            info.student.studentId
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase()) ||
            info.student.admissionNumber
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase());

          // Filter by status
          const matchesStatus =
            filterStatus === "all" || info.status === filterStatus;

          return matchesSearch && matchesStatus;
        });
    } else {
      // Total view - use all-time data from student.fees aggregation (with discounts properly applied)
      return studentsData.data
        .map((student: any) => {
          // Use fees data from student aggregation which properly accounts for discounts and scholarships
          const fees = student.fees || {};
          const totalDue =
            (fees.totalFeeStructure || 0) - (fees.scholarshipAmount || 0);
          const totalPaid = fees.totalPaid || 0;
          const balance = fees.totalBalance || 0;

          let status: "paid" | "partial" | "unpaid" | "overdue" = "unpaid";
          if (balance === 0 && totalDue > 0) {
            status = "paid";
          } else if (totalPaid > 0 && balance > 0) {
            status = "partial";
          } else if (balance < 0) {
            status = "overdue";
          }

          return {
            student,
            totalDue,
            totalPaid,
            balance,
            status,
            feeStatus: fees.feeStatus || status,
            scholarshipAmount: fees.scholarshipAmount || 0,
          };
        })
        .filter((info: any) => {
          // Filter by search term
          const matchesSearch =
            !activeSearchTerm ||
            info.student.userInfo?.firstName
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase()) ||
            info.student.userInfo?.lastName
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase()) ||
            info.student.studentId
              ?.toLowerCase()
              .includes(activeSearchTerm.toLowerCase());

          // Filter by status
          const matchesStatus =
            filterStatus === "all" || info.status === filterStatus;

          return matchesSearch && matchesStatus;
        });
    }
  }, [
    studentsData,
    feeSummariesData,
    activeSearchTerm,
    filterStatus,
    viewType,
  ]);

  // Statistics calculated from student fee data
  const statistics = useMemo(() => {
    const totalOutstanding = studentFeeInfo.reduce(
      (sum, info) => sum + info.balance,
      0
    );
    const totalCollected = studentFeeInfo.reduce(
      (sum, info) => sum + info.totalPaid,
      0
    );
    const pendingCount = studentFeeInfo.filter(
      (info) => info.status === "unpaid" || info.status === "partial"
    ).length;
    const overdueCount = studentFeeInfo.filter(
      (info) => info.status === "overdue"
    ).length;

    return {
      totalOutstanding,
      totalCollected,
      pendingCount,
      overdueCount,
    };
  }, [studentFeeInfo]);

  const formatCurrency = (amount: number) => {
    return `KSh ${amount.toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-sm";
      case "partial":
        return "bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-sm";
      case "overdue":
        return "bg-gradient-to-r from-red-400 to-pink-500 text-white shadow-sm";
      default:
        return "bg-gradient-to-r from-slate-300 to-slate-400 text-white shadow-sm";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="w-4 h-4" />;
      case "partial":
        return <Clock className="w-4 h-4" />;
      case "overdue":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleRecordPayment = (student: StudentWithUserInfo) => {
    setSelectedStudent(student);
    setPaymentModalOpen(true);
  };

  const handlePromptPayment = (student: StudentWithUserInfo) => {
    setSelectedStudentForPromptPayment(student);
    setPromptPaymentModalOpen(true);
  };

  const handleViewDetails = (student: StudentWithUserInfo) => {
    setSelectedStudentForDetails(student);
    setDetailsModalOpen(true);
  };

  const handlePrintReceipt = async (studentData: any, payment: any) => {
    try {
      if (
        !payment.referenceNumber &&
        (!payment.amount || !payment.paymentDate)
      ) {
        toast.error("Payment information is incomplete for printing");
        return;
      }

      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      let downloadUrl;

      if (payment.referenceNumber && payment.referenceNumber.trim() !== "") {
        // Use reference number if available
        downloadUrl = `${API_URL}/api/students/${studentData._id}/payment-receipt/${payment.referenceNumber}/download`;
      } else {
        // For payments without reference numbers, use amount and date as query parameters
        downloadUrl = `${API_URL}/api/students/${
          studentData._id
        }/payment-receipt/unknown/download?amount=${
          payment.amount
        }&paymentDate=${encodeURIComponent(payment.paymentDate)}`;
      }

      // Download receipt from backend
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to load receipt for printing: ${response.status}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create a URL for the PDF blob
      const pdfUrl = window.URL.createObjectURL(blob);

      // Open PDF in new window/tab for printing
      const printWindow = window.open(pdfUrl, "_blank");

      if (printWindow) {
        // Wait for the PDF to load, then trigger print
        printWindow.onload = () => {
          printWindow.print();
        };

        // Clean up the URL after printing
        printWindow.onbeforeunload = () => {
          window.URL.revokeObjectURL(pdfUrl);
        };

        toast.success("Receipt opened for printing!");
      } else {
        // Fallback: download the PDF if popup is blocked
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `Receipt-${payment.referenceNumber || "Unknown"}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Clean up the URL
        window.URL.revokeObjectURL(pdfUrl);

        toast.success("Receipt downloaded. Please open and print the PDF.");
      }
    } catch (error: any) {
      console.error("Print receipt error:", error);
      toast.error(error.message || "Failed to print receipt");
    }
  };

  const handleGenerateReceipt = async (studentData: any, payment: any) => {
    try {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const token = localStorage.getItem("token");

      let downloadUrl;

      if (payment.referenceNumber && payment.referenceNumber.trim() !== "") {
        // Use reference number if available
        downloadUrl = `${API_URL}/api/students/${studentData._id}/payment-receipt/${payment.referenceNumber}/download`;
      } else {
        // For payments without reference numbers, use amount and date as query parameters
        downloadUrl = `${API_URL}/api/students/${
          studentData._id
        }/payment-receipt/unknown/download?amount=${
          payment.amount
        }&paymentDate=${encodeURIComponent(payment.paymentDate)}`;
      }

      // Download receipt from backend using the student payment receipt endpoint
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to generate receipt: ${response.status}`
        );
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt-${payment.referenceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Receipt downloaded successfully");
    } catch (error: any) {
      console.error("Receipt generation error:", error);
      toast.error(error.message || "Failed to generate receipt");
    }
  };

  const handleSubmitPayment = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStudent) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    const studentFee = studentFeeInfo.find(
      (info) => info.student._id === selectedStudent._id
    );
    if (!studentFee) {
      toast.error("Student fee information not found");
      return;
    }

    // Note: Removed balance validation - reconciliation system handles overpayments as credit
    // Allow any amount - system will automatically:
    // 1. Pay outstanding invoices (oldest first)
    // 2. Create credit for any overpayment

    recordPaymentMutation.mutate({
      studentId: selectedStudent._id,
      amount,
      paymentMethod,
      reference: paymentReference || generatePaymentReference(),
      notes: `Manual payment recorded by secretary`,
    });
  };

  // Export handlers
  const handleExport = async () => {
    setIsExporting(true);
    try {
      let dataToExport: any[] = [];

      if (exportScope === "all") {
        // Export all student fee data without filters
        dataToExport = studentFeeInfo;
      } else {
        // Use current filtered data
        dataToExport = studentFeeInfo;
      }

      if (exportFormat === "excel") {
        exportStudentFeesToExcel(dataToExport);
      } else {
        exportStudentFeesToPDF(dataToExport);
      }

      toast.success(`Successfully exported to ${exportFormat.toUpperCase()}`);
      setShowExportModal(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const exportStudentFeesToExcel = (data: any[]) => {
    import("xlsx").then((XLSX) => {
      const worksheet = XLSX.utils.json_to_sheet(
        data.map((item: any) => ({
          "Student Name": `${item.student.userInfo?.firstName} ${item.student.userInfo?.lastName}`,
          "Admission Number":
            item.student.admissionNumber || item.student.studentId,
          "Expected Amount (KES)": item.totalDue || 0,
          "Amount Paid (KES)": item.totalPaid || 0,
          "Balance (KES)": item.balance || 0,
          Status:
            item.status === "paid"
              ? "Paid"
              : item.status === "partial"
              ? "Partial"
              : item.status === "overdue"
              ? "Overdue"
              : "Unpaid",
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Student Fees");

      const fileName = `student-fees-${
        viewType === "monthly" ? `${selectedYear}-${selectedMonth}` : "total"
      }-${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    });
  };

  const exportStudentFeesToPDF = (data: any[]) => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text("Student Fee Report", 14, 20);

    doc.setFontSize(11);
    doc.text(
      `Report Type: ${
        viewType === "monthly"
          ? `Monthly (${new Date(
              selectedYear,
              selectedMonth - 1
            ).toLocaleDateString("en-US", { month: "long", year: "numeric" })})`
          : "Total Since Registration"
      }`,
      14,
      28
    );
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 34);

    // Calculate totals
    const totalExpected = data.reduce(
      (sum, item) => sum + (item.totalDue || 0),
      0
    );
    const totalPaid = data.reduce(
      (sum, item) => sum + (item.totalPaid || 0),
      0
    );
    const totalBalance = data.reduce(
      (sum, item) => sum + (item.balance || 0),
      0
    );

    // Add summary
    doc.setFontSize(10);
    doc.text(`Total Expected: KES ${totalExpected.toLocaleString()}`, 14, 42);
    doc.text(`Total Paid: KES ${totalPaid.toLocaleString()}`, 14, 48);
    doc.text(`Total Balance: KES ${totalBalance.toLocaleString()}`, 14, 54);

    // Add table
    autoTable(doc, {
      startY: 60,
      head: [
        [
          "Student Name",
          "Admission No.",
          "Expected (KES)",
          "Paid (KES)",
          "Balance (KES)",
          "Status",
        ],
      ],
      body: data.map((item: any) => [
        `${item.student.userInfo?.firstName} ${item.student.userInfo?.lastName}`,
        item.student.admissionNumber || item.student.studentId,
        (item.totalDue || 0).toLocaleString(),
        (item.totalPaid || 0).toLocaleString(),
        (item.balance || 0).toLocaleString(),
        item.status === "paid"
          ? "Paid"
          : item.status === "partial"
          ? "Partial"
          : item.status === "overdue"
          ? "Overdue"
          : "Unpaid",
      ]),
      theme: "grid",
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 8 },
    });

    const fileName = `student-fees-${
      viewType === "monthly" ? `${selectedYear}-${selectedMonth}` : "total"
    }-${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  if (studentsLoading || feeSummariesLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading fee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 lg:p-6 space-y-4 lg:space-y-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 lg:p-6 border border-slate-200/60">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              Fee Management
              {viewType === "monthly" && (
                <span className="ml-2 text-sm font-semibold text-blue-600">
                  (
                  {new Date(selectedYear, selectedMonth - 1).toLocaleDateString(
                    "en-US",
                    { month: "long", year: "numeric" }
                  )}
                  )
                </span>
              )}
            </h1>
            <p className="text-slate-600 text-sm sm:text-base font-medium">
              {viewType === "total"
                ? "View all-time fee totals and balances"
                : "View fees for a specific month"}
            </p>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl transition-all duration-300 text-sm shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="space-y-3">
          {/* View Type Toggle */}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-xl border border-slate-300/60 bg-white p-1 shadow-sm">
              <button
                onClick={() => setViewType("total")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  viewType === "total"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                All Time Totals
              </button>
              <button
                onClick={() => setViewType("monthly")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  viewType === "monthly"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Monthly View
              </button>
            </div>

            {/* Month/Year Selectors (only show in monthly view) */}
            {viewType === "monthly" && (
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  <option value={1}>January</option>
                  <option value={2}>February</option>
                  <option value={3}>March</option>
                  <option value={4}>April</option>
                  <option value={5}>May</option>
                  <option value={6}>June</option>
                  <option value={7}>July</option>
                  <option value={8}>August</option>
                  <option value={9}>September</option>
                  <option value={10}>October</option>
                  <option value={11}>November</option>
                  <option value={12}>December</option>
                </select>
                <select
                  className="px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {Array.from(
                    { length: 5 },
                    (_, i) => new Date().getFullYear() - i
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Search and Status Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search students... (Press Enter to search)"
                className="w-full pl-10 pr-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm shadow-sm transition-all duration-300 font-medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <select
                className="px-3 sm:px-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700"
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    e.target.value as
                      | "all"
                      | "unpaid"
                      | "partial"
                      | "paid"
                      | "overdue"
                  )
                }
              >
                <option value="all">All Status</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
              <select
                className="px-3 sm:px-4 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm shadow-sm transition-all duration-300 font-semibold text-slate-700"
                value={selectedTermId}
                onChange={(e) => setSelectedTermId(e.target.value)}
              >
                <option value="all">All Terms</option>
                {academicTermsData?.data?.map((term: any) => (
                  <option key={term._id} value={term._id}>
                    {term.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60">
        <div className="px-6 py-4 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-900">
              Student Fee Records (
              {(viewType === "monthly"
                ? feeSummariesData?.pagination
                : studentsData?.pagination
              )?.totalItems || 0}{" "}
              total)
            </h2>
            <div className="text-sm text-slate-600 font-medium">
              Showing {studentFeeInfo.length} of{" "}
              {(viewType === "monthly"
                ? feeSummariesData?.pagination
                : studentsData?.pagination
              )?.totalItems || 0}
            </div>
          </div>
        </div>

        {studentFeeInfo.length === 0 ? (
          <div className="p-6 text-center">
            <Users className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-semibold text-slate-900">
              No students found
            </h3>
            <p className="mt-1 text-sm text-slate-600 font-medium">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              {/* Ensures minimum width for proper display */}
              <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Total Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Total Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-slate-200">
                {studentFeeInfo.map((info) => (
                  <tr
                    key={info.student._id}
                    className="hover:bg-blue-50/50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {info.student.photoUrl ||
                          info.student.userInfo?.profileDetails
                            ?.profilePicture ? (
                            <img
                              src={
                                info.student.photoUrl ||
                                info.student.userInfo?.profileDetails
                                  ?.profilePicture
                              }
                              alt={`${info.student.userInfo?.firstName} ${info.student.userInfo?.lastName}`}
                              className="h-10 w-10 rounded-xl object-cover shadow-md"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                                (
                                  e.target as HTMLImageElement
                                ).nextElementSibling?.classList.remove(
                                  "hidden"
                                );
                              }}
                            />
                          ) : null}
                          <div
                            className={`h-10 w-10 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-md ${
                              info.student.photoUrl ||
                              info.student.userInfo?.profileDetails
                                ?.profilePicture
                                ? "hidden"
                                : ""
                            }`}
                          >
                            <span className="text-sm font-bold text-white">
                              {info.student.userInfo?.firstName?.charAt(0)}
                              {info.student.userInfo?.lastName?.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-slate-900">
                            {info.student.userInfo?.firstName}{" "}
                            {info.student.userInfo?.lastName}
                          </div>
                          <div className="text-sm text-slate-600 font-medium">
                            {info.student.admissionNumber}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {formatCurrency(info.totalDue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {formatCurrency(info.totalPaid)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {formatCurrency(info.balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(
                          info.status
                        )}`}
                      >
                        {getStatusIcon(info.status)}
                        <span className="ml-1 capitalize">{info.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {info.balance > 0 && (
                          <>
                            <button
                              onClick={() => handleRecordPayment(info.student)}
                              className="flex items-center px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Record Payment
                            </button>
                            <button
                              onClick={() => handlePromptPayment(info.student)}
                              disabled
                              className="flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
                            >
                              <CreditCard className="w-3 h-3 mr-1" />
                              Prompt Payment
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleViewDetails(info.student)}
                          className="flex items-center px-3 py-1.5 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          View Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {studentFeeInfo.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
            <Pagination
              currentPage={currentPage}
              totalPages={
                (viewType === "monthly"
                  ? feeSummariesData?.pagination
                  : studentsData?.pagination
                )?.totalPages || 1
              }
              onPageChange={setCurrentPage}
              pageSize={itemsPerPage}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
              showPageSizeSelector={true}
              className="w-full"
            />
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModalOpen && selectedStudent && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200/60">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                Record Payment
              </h3>
              <button
                onClick={() => setPaymentModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
              <p className="text-sm font-semibold text-blue-600 mb-2">
                Student:
              </p>
              <p className="font-bold text-slate-900">
                {selectedStudent.userInfo?.firstName}{" "}
                {selectedStudent.userInfo?.lastName}
              </p>
              <p className="text-sm text-slate-600 font-medium mt-1">
                ADM: {selectedStudent.admissionNumber}
              </p>
              <p className="text-sm text-slate-900 font-semibold mt-2">
                Current Balance:{" "}
                <span className="text-blue-600">
                  {formatCurrency(
                    studentFeeInfo.find(
                      (info) => info.student._id === selectedStudent._id
                    )?.balance || 0
                  )}
                </span>
              </p>
              <div className="mt-3 p-2 bg-blue-100/50 rounded-lg">
                <p className="text-xs text-blue-700 font-medium">
                  💡 Tip: Payment will be automatically applied to oldest unpaid
                  invoices first. Any overpayment will be saved as credit for
                  future invoices.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmitPayment} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Payment Amount (KSh)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all duration-300 font-medium"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Payment Method
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  value={paymentMethod}
                  onChange={(e) =>
                    setPaymentMethod(
                      e.target.value as
                        | "cash"
                        | "bank_transfer"
                        | "cheque"
                        | "mpesa"
                        | "card"
                        | "online"
                    )
                  }
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="mpesa">M-Pesa</option>
                  <option value="card">Card</option>
                  <option value="online">Online</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Reference Number (Optional)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-slate-300/60 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm transition-all duration-300 font-medium"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Transaction/Receipt reference"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setPaymentModalOpen(false)}
                  className="px-4 py-2 text-slate-700 border border-slate-300/60 rounded-xl hover:bg-slate-50 transition-all duration-300 font-semibold shadow-sm"
                  disabled={recordPaymentMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
                  disabled={recordPaymentMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {recordPaymentMutation.isPending
                    ? "Recording..."
                    : "Record Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {detailsModalOpen && selectedStudentForDetails && (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-slate-200/60">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200/60">
              <div>
                <h3 className="text-xl font-bold text-slate-900">
                  Payment Details
                </h3>
                <p className="text-sm text-slate-600 font-medium mt-1">
                  {selectedStudentForDetails.userInfo?.firstName}{" "}
                  {selectedStudentForDetails.userInfo?.lastName}
                </p>
              </div>
              <button
                onClick={() => setDetailsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[75vh]">
              {paymentHistoryLoading ? (
                <div className="flex justify-center py-8">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-emerald-500"></div>
                </div>
              ) : paymentHistoryData?.success ? (
                <div className="space-y-6">
                  {/* Fee Summary */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200/60">
                    <h4 className="text-lg font-bold text-slate-900 mb-4">
                      Fee Summary
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-blue-600">
                          Total Expected
                        </p>
                        <p className="text-lg font-bold text-blue-900">
                          KSh{" "}
                          {(
                            paymentHistoryData.data.feeSummary
                              .totalFeeStructure || 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-emerald-600">
                          Total Paid
                        </p>
                        <p className="text-lg font-bold text-emerald-900">
                          KSh{" "}
                          {(
                            paymentHistoryData.data.feeSummary.totalPaid || 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-red-600">
                          Balance
                        </p>
                        <p className="text-lg font-bold text-red-900">
                          KSh{" "}
                          {(
                            paymentHistoryData.data.feeSummary.totalBalance || 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-purple-600">
                          Status
                        </p>
                        <p className="text-lg font-bold capitalize text-purple-900">
                          {paymentHistoryData.data.feeSummary.feeStatus ||
                            "N/A"}
                        </p>
                      </div>
                    </div>
                    {(paymentHistoryData.data.feeSummary.creditBalance || 0) >
                      0 && (
                      <div className="mt-4 text-center p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-sm font-semibold text-green-600">
                          💰 Credit Balance (Prepayment)
                        </p>
                        <p className="text-lg font-bold text-green-900">
                          KSh{" "}
                          {(
                            paymentHistoryData.data.feeSummary.creditBalance ||
                            0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          This will be applied to future invoices
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Invoice Details */}
                  {paymentHistoryData.data.invoices &&
                  paymentHistoryData.data.invoices.length > 0 ? (
                    <div>
                      <h4 className="text-lg font-bold text-slate-900 mb-4">
                        Invoice Details (
                        {paymentHistoryData.data.invoices.length} invoices)
                      </h4>
                      <div className="space-y-3">
                        {paymentHistoryData.data.invoices
                          .sort((a: any, b: any) => {
                            const dateA = new Date(
                              a.dueDate || a.createdAt
                            ).getTime();
                            const dateB = new Date(
                              b.dueDate || b.createdAt
                            ).getTime();
                            return dateB - dateA; // Newest first
                          })
                          .map((invoice: any, index: number) => {
                            // Use periodMonth and periodYear if available, otherwise fall back to createdAt
                            let monthLabel = "";
                            if (invoice.periodMonth && invoice.periodYear) {
                              const periodDate = new Date(
                                invoice.periodYear,
                                invoice.periodMonth - 1,
                                1
                              );
                              monthLabel = periodDate.toLocaleDateString(
                                "en-US",
                                { month: "long", year: "numeric" }
                              );
                            } else if (invoice.periodLabel) {
                              monthLabel = invoice.periodLabel;
                            } else {
                              // Fallback to createdAt for display
                              const invoiceDate = new Date(
                                invoice.createdAt || invoice.dueDate
                              );
                              monthLabel = invoiceDate.toLocaleDateString(
                                "en-US",
                                { month: "long", year: "numeric" }
                              );
                            }

                            // Calculate amounts properly accounting for discounts and scholarships
                            // Balance = totalAmount - discountAmount - scholarshipAmount - amountPaid
                            const totalAmount = invoice.totalAmount || 0;
                            const discountAmount = invoice.discountAmount || 0;
                            const scholarshipAmount =
                              invoice.scholarshipAmount || 0;
                            const paid = invoice.amountPaid || 0;
                            const expected =
                              totalAmount - discountAmount - scholarshipAmount;
                            const balance = expected - paid;

                            const status =
                              balance === 0 && expected > 0
                                ? "paid"
                                : paid > 0 && balance > 0
                                ? "partial"
                                : "unpaid";

                            return (
                              <div
                                key={index}
                                className="bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-xl p-4"
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="font-bold text-slate-900">
                                      {monthLabel}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <p className="text-sm text-slate-600 capitalize">
                                        {invoice.type || "monthly"} Invoice
                                      </p>
                                      {invoice.invoiceNumber && (
                                        <span className="text-xs text-slate-500">
                                          #{invoice.invoiceNumber}
                                        </span>
                                      )}
                                      {(discountAmount > 0 ||
                                        scholarshipAmount > 0) && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                                          {discountAmount > 0 &&
                                          scholarshipAmount > 0
                                            ? `Discount + Scholarship`
                                            : discountAmount > 0
                                            ? `${Math.round(
                                                (discountAmount / totalAmount) *
                                                  100
                                              )}% Discount`
                                            : `Scholarship`}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                                      status === "paid"
                                        ? "bg-green-100 text-green-700"
                                        : status === "partial"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {status}
                                  </span>
                                </div>
                                <div className="grid grid-cols-3 gap-3 mt-3">
                                  <div className="text-center p-2 bg-blue-50 rounded-lg">
                                    <p className="text-xs text-blue-600 font-semibold">
                                      Expected
                                    </p>
                                    <p className="text-sm font-bold text-blue-900">
                                      KSh {expected.toLocaleString()}
                                    </p>
                                    {(discountAmount > 0 ||
                                      scholarshipAmount > 0) && (
                                      <p className="text-xs text-blue-500 mt-1">
                                        (Was: {totalAmount.toLocaleString()})
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-center p-2 bg-green-50 rounded-lg">
                                    <p className="text-xs text-green-600 font-semibold">
                                      Paid
                                    </p>
                                    <p className="text-sm font-bold text-green-900">
                                      KSh {paid.toLocaleString()}
                                    </p>
                                  </div>
                                  <div className="text-center p-2 bg-red-50 rounded-lg">
                                    <p className="text-xs text-red-600 font-semibold">
                                      Balance
                                    </p>
                                    <p className="text-sm font-bold text-red-900">
                                      KSh {balance.toLocaleString()}
                                    </p>
                                  </div>
                                </div>

                                {/* Show discount/scholarship breakdown if applicable */}
                                {(discountAmount > 0 ||
                                  scholarshipAmount > 0) && (
                                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-xs text-green-700 font-semibold mb-1">
                                      💰 Savings Applied:
                                    </p>
                                    {discountAmount > 0 && (
                                      <p className="text-xs text-green-600">
                                        • Discount: KSh{" "}
                                        {discountAmount.toLocaleString()}
                                      </p>
                                    )}
                                    {scholarshipAmount > 0 && (
                                      <p className="text-xs text-green-600">
                                        • Scholarship: KSh{" "}
                                        {scholarshipAmount.toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                )}

                                {/* Debug info - remove after fixing */}
                                {expected === 0 && paid > 0 && (
                                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                                    <p className="text-yellow-700 font-semibold">
                                      ⚠️ Data Issue: This invoice has payments
                                      but no expected amount set
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-xl">
                      <p className="text-slate-600 font-medium">
                        No invoices available
                      </p>
                    </div>
                  )}

                  {/* Payment History */}
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 mb-4">
                      Payment History (
                      {paymentHistoryData.data.feeSummary.totalPayments}{" "}
                      payments)
                    </h4>
                    {paymentHistoryData.data.paymentHistory.length > 0 ? (
                      <div className="space-y-3">
                        {paymentHistoryData.data.paymentHistory.map(
                          (payment, index) => (
                            <div
                              key={index}
                              className="bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-4 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-4">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                                      <DollarSign className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <p className="font-bold text-slate-900">
                                        KSh{" "}
                                        {(payment.amount || 0).toLocaleString()}
                                      </p>
                                      <p className="text-sm text-slate-600 font-semibold capitalize">
                                        {(
                                          payment.paymentMethod || "unknown"
                                        ).replace("_", " ")}
                                      </p>
                                      {payment.invoicesPaid &&
                                        payment.invoicesPaid.length > 0 && (
                                          <p className="text-xs text-emerald-600 font-medium mt-1">
                                            ✓ Applied to{" "}
                                            {payment.invoicesPaid.length}{" "}
                                            invoice(s)
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                  {payment.referenceNumber && (
                                    <p className="mt-2 text-sm text-slate-600 font-medium">
                                      Ref: {payment.referenceNumber}
                                    </p>
                                  )}
                                  {payment.notes && (
                                    <p className="mt-1 text-sm text-slate-600 font-medium">
                                      {payment.notes}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3">
                                  <div className="text-right">
                                    <p className="text-sm text-slate-600 font-semibold">
                                      {new Date(
                                        payment.paymentDate
                                      ).toLocaleDateString()}
                                    </p>
                                    <p className="text-xs text-slate-500 font-medium">
                                      {new Date(
                                        payment.paymentDate
                                      ).toLocaleTimeString()}
                                    </p>
                                  </div>
                                  <div className="flex flex-col space-y-2">
                                    <button
                                      onClick={() =>
                                        handleGenerateReceipt(
                                          paymentHistoryData.data.student,
                                          payment
                                        )
                                      }
                                      className="px-3 py-1.5 text-xs bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
                                    >
                                      <Download className="w-3 h-3 inline mr-1" />
                                      Download
                                    </button>
                                    <button
                                      onClick={() =>
                                        handlePrintReceipt(
                                          paymentHistoryData.data.student,
                                          payment
                                        )
                                      }
                                      className="px-3 py-1.5 text-xs bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105 font-semibold"
                                    >
                                      <Printer className="w-3 h-3 inline mr-1" />
                                      Print
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                          <CreditCard className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-slate-600 font-medium">
                          No payments recorded yet
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Student Information */}
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200/60">
                    <h4 className="text-lg font-bold text-slate-900 mb-4">
                      Student Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-slate-600">
                          Student ID
                        </p>
                        <p className="font-bold text-slate-900">
                          {paymentHistoryData.data.student.studentId}
                        </p>
                      </div>
                      <div className="p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-slate-600">
                          Class
                        </p>
                        <p className="font-bold text-slate-900">
                          {paymentHistoryData.data.student.class?.name ||
                            "Not assigned"}
                        </p>
                      </div>
                      <div className="p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-slate-600">
                          Email
                        </p>
                        <p className="font-bold text-slate-900">
                          {paymentHistoryData.data.student.userInfo?.email}
                        </p>
                      </div>
                      <div className="p-3 bg-white/70 backdrop-blur-sm rounded-xl">
                        <p className="text-sm font-semibold text-slate-600">
                          Enrollment Date
                        </p>
                        <p className="font-bold text-slate-900">
                          {new Date(
                            paymentHistoryData.data.student.enrollmentDate
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-slate-600 font-medium">
                    Failed to load payment details
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200/60">
              <div className="flex justify-end">
                <button
                  onClick={() => setDetailsModalOpen(false)}
                  className="px-4 py-2 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {receiptModalOpen && lastPaymentData && (
        <ReceiptModal
          isOpen={receiptModalOpen}
          onClose={() => setReceiptModalOpen(false)}
          student={lastPaymentData.student}
          payment={lastPaymentData.payment}
          receiptNumber={lastPaymentData.receiptNumber}
          branchInfo={{
            name: "ATIAM COLLEGE", // You can get this from user context or branch data
            address: "Komarock Sector 3A, Nairobi",
            phone: "+1 (555) 123-4567",
            email: "admissions@atiamcollege.com",
          }}
        />
      )}

      {/* Prompt Payment Modal */}
      {promptPaymentModalOpen && selectedStudentForPromptPayment && (
        <PromptPaymentModal
          isOpen={promptPaymentModalOpen}
          onClose={() => setPromptPaymentModalOpen(false)}
          onPaymentSuccess={() => {
            setPromptPaymentModalOpen(false);
            // Refresh the students data
            window.location.reload();
          }}
          preselectedStudent={selectedStudentForPromptPayment}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
              onClick={() => setShowExportModal(false)}
            ></div>

            <div className="inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-6 pt-6 pb-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl p-2.5 shadow-lg">
                      <Download className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Export Data
                      </h3>
                      <p className="text-sm text-slate-600 font-medium">
                        Choose format and scope
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Export Format */}
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Export Format
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setExportFormat("excel")}
                        className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                          exportFormat === "excel"
                            ? "bg-gradient-to-br from-emerald-300 to-green-400 border-emerald-400 text-white shadow-lg"
                            : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                        }`}
                      >
                        <FileText className="h-5 w-5 mx-auto mb-1" />
                        Excel
                      </button>
                      <button
                        onClick={() => setExportFormat("pdf")}
                        className={`px-4 py-3 rounded-xl font-bold transition-all border-2 ${
                          exportFormat === "pdf"
                            ? "bg-gradient-to-br from-red-300 to-red-400 border-red-400 text-white shadow-lg"
                            : "bg-white border-slate-200 text-slate-700 hover:border-red-300"
                        }`}
                      >
                        <FileText className="h-5 w-5 mx-auto mb-1" />
                        PDF
                      </button>
                    </div>
                  </div>

                  {/* Export Scope */}
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Export Scope
                    </label>
                    <div className="space-y-2">
                      <button
                        onClick={() => setExportScope("filtered")}
                        className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                          exportScope === "filtered"
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                            : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold">Current View</p>
                            <p className="text-xs mt-1">
                              Export filtered results ({studentFeeInfo.length}{" "}
                              records)
                            </p>
                          </div>
                          <Search className="h-5 w-5" />
                        </div>
                      </button>
                      <button
                        onClick={() => setExportScope("all")}
                        className={`w-full px-4 py-3 rounded-xl font-bold transition-all border-2 text-left ${
                          exportScope === "all"
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 text-slate-900"
                            : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold">All Data</p>
                            <p className="text-xs mt-1">
                              Export all student fee records
                            </p>
                          </div>
                          <Users className="h-5 w-5" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 flex gap-3 border-t border-slate-200">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-600 rounded-xl font-bold text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Export
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretaryFees;
